import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Ban, Briefcase, Loader2, RefreshCw, X } from 'lucide-react';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import {
    ASSIGNMENT_STATUS_KLASSEN,
    ASSIGNMENT_STATUS_LABELS,
    FreelancerProjectAccess,
    InspectionAssignment,
    canAdminCancel,
    formatDatum,
    formatZeitraum,
    sortiereAssignments,
    summeVerguetungCents,
} from '../../services/assignmentLogic';
import {
    ProjektKopf,
    cancelAssignment,
    fetchAssignments,
    fetchFreelancerAccess,
    fetchProjektKoepfe,
} from '../../services/assignmentService';
import { formatCents } from '../../services/freelancerLogic';

// Abschnitt „Aufträge & Projekte" der Freelancer-Akte (ERP/CRM-Plan,
// Abschnitt 7 — Phase D2, Admin-Sicht).
//
// Gegenrichtung zum ProjectAccessPanel: dort steht ein Projekt und die Frage
// „wer arbeitet hier?", hier steht eine Person und die Frage „woran arbeitet
// sie?". Freischalten passiert bewusst NICHT hier — eine Freischaltung ist
// eine Projektentscheidung und gehoert an das Projekt (Plan 7, Workflow
// Schritt 1: „UI im ProjectHub").
//
// Duenner Container (Standing Decision): Sortierung, Statusfarben, Storno-
// Erlaubnis und Summen kommen fertig aus assignmentLogic.ts.

interface Props {
    freelancerId: string;
}

export const FreelancerAssignmentsSection: React.FC<Props> = ({ freelancerId }) => {
    const [zugaenge, setZugaenge] = useState<FreelancerProjectAccess[]>([]);
    const [auftraege, setAuftraege] = useState<InspectionAssignment[]>([]);
    const [projekte, setProjekte] = useState<Map<string, ProjektKopf>>(new Map());
    const [laedt, setLaedt] = useState(true);
    const [fehler, setFehler] = useState<string | null>(null);
    const [busy, setBusy] = useState<string | null>(null);

    const laden = useCallback(async () => {
        if (!freelancerId) return;
        setLaedt(true);
        setFehler(null);
        try {
            const [zugriff, jobs] = await Promise.all([
                fetchFreelancerAccess(freelancerId),
                fetchAssignments({ freelancerId }),
            ]);
            setZugaenge(zugriff);
            setAuftraege(jobs);
            // Projektnamen erst danach und in EINER Abfrage — die Ids aus
            // beiden Listen zusammen, damit kein N+1 entsteht.
            setProjekte(
                await fetchProjektKoepfe([
                    ...zugriff.map((z) => z.projectId),
                    ...jobs.map((a) => a.projectId),
                ]),
            );
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setLaedt(false);
        }
    }, [freelancerId]);

    useEffect(() => {
        void laden();
    }, [laden]);

    const auftraegeSortiert = useMemo(() => sortiereAssignments(auftraege), [auftraege]);
    const summeOffen = useMemo(
        () => summeVerguetungCents(auftraege, ['angeboten', 'angenommen']),
        [auftraege],
    );

    const projektName = (id: string): string => {
        const p = projekte.get(id);
        if (!p) return 'Unbekanntes Projekt';
        return p.projectNumber ? `${p.projectNumber} · ${p.name}` : p.name;
    };

    const stornieren = async (a: InspectionAssignment) => {
        setFehler(null);
        setBusy(a.id);
        try {
            const aktualisiert = await cancelAssignment(a.id);
            if (aktualisiert) {
                setAuftraege((liste) => liste.map((x) => (x.id === a.id ? aktualisiert : x)));
            }
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setBusy(null);
        }
    };

    return (
        <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                        <Briefcase className="w-4 h-4 text-slate-400 shrink-0" />
                        Aufträge & Projekte
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                        Freigeschaltet wird am Projekt (ProjectHub → „Freelancer & Begehungsaufträge").
                        Hier steht, was daraus geworden ist.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => void laden()}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 shrink-0"
                    aria-label="Neu laden"
                >
                    <RefreshCw className="w-4 h-4" />
                </button>
            </div>

            {fehler && (
                <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded-xl p-3 text-sm flex items-start justify-between gap-3">
                    <span className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                        {fehler}
                    </span>
                    <button onClick={() => setFehler(null)} className="shrink-0 hover:bg-rose-100 rounded p-1">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {laedt ? (
                <p className="py-8 text-center text-sm text-slate-400">
                    <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
                    Aufträge werden geladen…
                </p>
            ) : (
                <>
                    {/* ── Freischaltungen ── */}
                    <div>
                        <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
                            Aktive Freischaltungen ({zugaenge.length})
                        </h4>
                        {zugaenge.length === 0 ? (
                            <p className="py-4 text-center text-xs text-slate-400 border-2 border-dashed border-slate-100 rounded-2xl">
                                Für kein Projekt freigeschaltet.
                            </p>
                        ) : (
                            <ul className="flex flex-wrap gap-2">
                                {zugaenge.map((z) => (
                                    <li
                                        key={z.id}
                                        className="px-3 py-1.5 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-600"
                                        title={`Freigeschaltet am ${formatDatum(z.grantedAt)}`}
                                    >
                                        {projektName(z.projectId)}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* ── Aufträge ── */}
                    <div>
                        <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
                            Begehungsaufträge ({auftraegeSortiert.length})
                        </h4>
                        {auftraegeSortiert.length === 0 ? (
                            <p className="py-4 text-center text-xs text-slate-400 border-2 border-dashed border-slate-100 rounded-2xl">
                                Noch kein Begehungsauftrag erteilt.
                            </p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-[11px] uppercase tracking-widest text-slate-400 border-b border-slate-100">
                                            <th className="py-2 pr-3 font-bold">Projekt</th>
                                            <th className="py-2 pr-3 font-bold">Zeitraum</th>
                                            <th className="py-2 pr-3 font-bold">Vergütung</th>
                                            <th className="py-2 pr-3 font-bold">Status</th>
                                            <th className="py-2 font-bold text-right">Aktion</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {auftraegeSortiert.map((a) => {
                                            const arbeitet = busy === a.id;
                                            return (
                                                <tr key={a.id} className="border-b border-slate-50 last:border-0">
                                                    <td className="py-2.5 pr-3 text-slate-700 font-bold">
                                                        {projektName(a.projectId)}
                                                    </td>
                                                    <td className="py-2.5 pr-3 text-slate-600 whitespace-nowrap">
                                                        {formatZeitraum(a.zeitraumVon, a.zeitraumBis)}
                                                    </td>
                                                    <td className="py-2.5 pr-3 text-slate-600 tabular-nums whitespace-nowrap">
                                                        {formatCents(a.verguetungCents)}
                                                    </td>
                                                    <td className="py-2.5 pr-3">
                                                        <span
                                                            className={`inline-block px-2 py-0.5 rounded-full border text-[11px] font-bold ${ASSIGNMENT_STATUS_KLASSEN[a.status]}`}
                                                        >
                                                            {ASSIGNMENT_STATUS_LABELS[a.status]}
                                                        </span>
                                                    </td>
                                                    <td className="py-2.5 text-right">
                                                        {canAdminCancel(a) ? (
                                                            <button
                                                                type="button"
                                                                onClick={() => void stornieren(a)}
                                                                disabled={arbeitet}
                                                                className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-500 hover:text-rose-600 disabled:opacity-50"
                                                            >
                                                                {arbeitet ? (
                                                                    <Loader2 className="w-3 h-3 animate-spin" />
                                                                ) : (
                                                                    <Ban className="w-3 h-3" />
                                                                )}
                                                                Stornieren
                                                            </button>
                                                        ) : (
                                                            <span className="text-[11px] text-slate-300">—</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                {summeOffen > 0 && (
                                    <p className="mt-2 text-[11px] text-slate-500 text-right">
                                        Angebotene und angenommene Aufträge:{' '}
                                        <strong className="text-slate-700">{formatCents(summeOffen)}</strong>
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                </>
            )}
        </section>
    );
};
